/**
 * Zustand slice — per-flow draft state for the four-step onboarding.
 *
 * Survives intra-tab navigation (S01 → S02 → S03 → S04 → S05) without
 * round-tripping through the URL for every value. Does NOT survive page
 * reload — that's intentional. The server is the source of truth for anything
 * persistent (session cookie after OTP, company row after createWorkspace).
 *
 * Once the identity vertical lands and `trpc.identity.me` returns the active
 * principal + company on cold start, this slice can drop most of its fields
 * and become a small bag of one-off draft values (`gstinDraft`).
 *
 * Architecture §8.4: cross-component UI state → Zustand.
 */
import { create } from "zustand";

export interface OnboardingFlowState {
  /** Raw 10-digit phone — set by S01, read by S02 for the masked display. */
  phone: string | null;
  /** OTP request id returned by `identity.requestOtp` — handed to verifyOtp. */
  challengeId: string | null;
  /** Set after `identity.verifyOtp` succeeds — used by S03 to pre-fill workspace.email. */
  userId: string | null;
  /** Returned by `identity.createWorkspace` — read by S04 + S05. */
  companyId: string | null;
  /** Trial end-date (YYYY-MM-DD) returned by createWorkspace — shown in S05. */
  trialEndsOn: string | null;

  setPhone: (phone: string) => void;
  setChallenge: (challengeId: string) => void;
  setVerified: (userId: string) => void;
  setWorkspace: (input: { companyId: string; trialEndsOn: string }) => void;
  reset: () => void;
}

const empty = {
  phone: null,
  challengeId: null,
  userId: null,
  companyId: null,
  trialEndsOn: null,
};

export const useOnboardingFlow = create<OnboardingFlowState>((set) => ({
  ...empty,
  setPhone: (phone) => set({ phone }),
  setChallenge: (challengeId) => set({ challengeId }),
  setVerified: (userId) => set({ userId }),
  setWorkspace: ({ companyId, trialEndsOn }) => set({ companyId, trialEndsOn }),
  reset: () => set(empty),
}));
